/**
 * ListStore - store для управління списками
 *
 * Відповідає за:
 * - Сортування (sorter)
 * - Фільтрацію (filter, prefilter)
 * - Пагінацію (pagination)
 * - Завантаження даних
 * - Дії списку
 * - Управління станом через MobX
 *
 * Примітка: UI рендеринг керується через UIManager в List.tsx
 */

import type { IListActions, IListPagination, IListSorter, ListStoreProps } from '@/Core/Contracts/UI/List'

import { UIManager } from '@/Core/Managers/UI'
import { action, makeObservable, observable } from 'mobx'

/**
 * Комплексний клас сховища для управління списковими UI компонентами
 *
 * Розширює UIManager і забезпечує керування спостережуваним станом через MobX для операцій зі списками:
 * - Фільтрацію даних з підтримкою як звичайних, так і попередніх фільтрів
 * - Функціональність сортування
 * - Пагінацію з елементами навігації
 * - Асинхронне завантаження даних зі станами завантаження
 * - Управління метаданими
 * - Налаштовувані дії для операцій зі списками
 *
 * @extends UIManager
 *
 * @example
 * ```typescript
 * const listStore = new ListStore({
 *   restUrl: '/api/users',
 *   pagination: { current: 1, pageSize: 20, total: 0 },
 *   filter: { status: 'active' },
 *   sorter: { key: 'name', order: 'ascend' },
 *   template: MyListTemplate
 * })
 * ```
 */
export class ListStore extends UIManager {
    /** URI для REST запитів */
    restUrl: string = ''

    /** Ключ для кешування запиту */
    restKey: string[] = []

    /** Додаткові параметри запиту */
    restSearchParams: Record<string, any> = {}

    /** Початкове сортування */
    sorter: IListSorter = {}

    /** Початковий фільтр */
    filter: Record<string, any> = {}

    /** Преафільтр (незмінний) */
    prefilter: Record<string, any> = {}

    /** Пагінація */
    pagination: IListPagination = {
        current: 1,
        pageSize: 10,
        total: 0,
    }

    /** Дії списку */
    actions: IListActions = {
        load: () => {},
        reload: () => {},
        loadNextPage: () => {},
        loadPrevPage: () => {},
    }

    // Додаткові властивості для внутрішнього стану

    /** Чи є наступна сторінка */
    issetNextPage: boolean = false

    /** Чи є попередня сторінка */
    issetPrevPage: boolean = false

    /** Додаткові метадані списку */
    meta: Record<string, any> = {}

    /** Елементи списку */
    items: any[] = []

    /** Чи відбувається завантаження */
    isLoading: boolean = false

    /** Пошуковий запит */
    search: string = ''

    constructor(config: ListStoreProps = {}) {
        // Деструктуризація List-специфічних властивостей
        const {
            sorter,
            filter,
            prefilter,
            pagination,
            restUrl,
            restKey,
            restSearchParams,
            restSearchParamsTransformer,
            requestTransformer,
            responseTransformer,
            disableResponseTransform,
            disableRequestTransform,
            actions,
            ...uiManagerConfig
        } = config

        // Передаємо UIManager властивості базовому класу
        super(uiManagerConfig)

        // Ініціалізуємо List-специфічні властивості
        if (sorter) this.setSorter(sorter)
        if (filter) this.setFilter(filter)
        if (prefilter) this.setPrefilter(prefilter)
        if (pagination) this.setPagination(pagination)
        if (restUrl) this.restUrl = restUrl
        if (restKey) this.restKey = restKey
        if (restSearchParams) this.restSearchParams = restSearchParams

        makeObservable(this, {
            meta: observable,
            items: observable,
            isLoading: observable,
            pagination: observable,
            filter: observable,
            sorter: observable,
            actions: observable,
            issetNextPage: observable,
            issetPrevPage: observable,
            search: observable,
            setMeta: action,
            setItems: action,
            setIsLoading: action,
            setTotal: action,
            setFilter: action,
            setPagination: action,
            setSorter: action,
            setActions: action,
            setFilterOne: action,
            resetFilter: action,
            setCurrentPage: action,
            setPageSize: action,
            setIssetNextPage: action,
            setIssetPrevPage: action,
            setSearch: action,
        })
    }

    /**
     * Повний фільтр (filter + prefilter)
     */
    get fullFilter(): Record<string, any> {
        return { ...this.filter, ...this.prefilter }
    }

    // ==================== List-specific Methods ====================

    /**
     * Встановлює чи є наступна сторінка
     */
    setIssetNextPage(value: boolean): void {
        this.issetNextPage = value
    }

    /**
     * Встановлює чи є попередня сторінка
     */
    setIssetPrevPage(value: boolean): void {
        this.issetPrevPage = value
    }

    /**
     * Встановлює загальну кількість елементів
     */
    setTotal(total: number): void {
        if (this.pagination) {
            this.pagination.total = total
        }
    }

    /**
     * Встановлює фільтр
     */
    setFilter(filter: Record<string, any>): void {
        this.filter = filter
    }

    /**
     * Встановлює преафільтр
     */
    setPrefilter(prefilter: Record<string, any>): void {
        this.prefilter = prefilter
    }

    /**
     * Встановлює пагінацію
     */
    setPagination(pagination: IListPagination): void {
        this.pagination = { ...this.pagination, ...pagination }
    }

    /**
     * Встановлює сортування
     */
    setSorter(sorter: IListSorter): void {
        this.sorter = sorter
    }

    /**
     * Встановлює одне значення фільтру
     */
    setFilterOne(key: string, value: any): void {
        if (key === undefined) return

        if (value === undefined) {
            delete this.filter[key]
        } else {
            this.filter[key] = value
        }
    }

    /**
     * Скидає фільтр
     */
    resetFilter(): void {
        this.filter = {}
    }

    /**
     * Встановлює поточну сторінку
     */
    setCurrentPage(number: number): void {
        this.pagination.current = number
    }

    /**
     * Встановлює кількість елементів на сторінці
     */
    setPageSize(size: number): void {
        this.pagination.pageSize = size
    }

    /**
     * Встановлює дії списку
     */
    setActions(actions: Partial<IListActions>): void {
        this.actions = { ...this.actions, ...actions }
    }

    /**
     * Встановлює метадані списку
     */
    setMeta(meta: Record<string, any>): void {
        this.meta = meta
    }

    setItems(items: any[]): void {
        this.items = items
    }

    setIsLoading(isLoading: boolean): void {
        this.isLoading = isLoading
    }

    /**
     * Встановлює пошуковий запит
     */
    setSearch(search: string): void {
        this.search = search
    }
}
